import React, { useState, useEffect, useRef, FC } from "react";
import { Loader2, ChevronDown, Headphones } from "lucide-react";
import { Modality } from "@google/genai";
import { functions } from "../../firebase";
import { VOICE_OPTIONS, VOICE_PREVIEW_URLS, VOICE_PRESETS, VOICE_LANGUAGES, VOICE_TONES, VOICE_GENDERS, VOICE_SPEEDS, type GeneratedVoice } from "./constants";
import { decode, pcmToMp3Blob } from "./helpers";
import GeneratedVoiceItem from "./GeneratedVoiceItem";

const callGemini = functions.httpsCallable('callGemini');

interface VoiceoverTabProps {
    onAddLibraryItem?: (item: any, category: any, mode?: string) => void;
    generatedVoices: GeneratedVoice[];
    setGeneratedVoices: React.Dispatch<React.SetStateAction<GeneratedVoice[]>>;
    voiceoverText: string;
    setVoiceoverText: React.Dispatch<React.SetStateAction<string>>;
}

const VoiceoverTab: FC<VoiceoverTabProps> = ({ onAddLibraryItem = () => { }, generatedVoices, setGeneratedVoices, voiceoverText, setVoiceoverText }) => {
    const [voice, setVoice] = useState(VOICE_OPTIONS[0]);
    const [language, setLanguage] = useState(VOICE_LANGUAGES[0].code);
    const [tone, setTone] = useState(VOICE_TONES[0].id);
    const [useProModel, setUseProModel] = useState(true);
    const [playError, setPlayError] = useState("");
    const previewAudioRef = useRef<HTMLAudioElement | null>(null);
    const [isVoiceDropdownOpen, setIsVoiceDropdownOpen] = useState(false);
    const voiceDropdownRef = useRef<HTMLDivElement>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [genderFilter, setGenderFilter] = useState("all");
    const [speed, setSpeed] = useState("normal");

    // Filter voices by gender
    const filteredVoiceOptions = genderFilter === "all"
        ? VOICE_PRESETS
        : VOICE_PRESETS.filter(v => v.gender === genderFilter);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (voiceDropdownRef.current && !(voiceDropdownRef.current as any).contains(event.target as any)) {
                setIsVoiceDropdownOpen(false);
            }
        };
        window.document.addEventListener("mousedown", handleClickOutside);
        return () => window.document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        return () => stopPreview();
    }, []);

    const ensurePreviewAudio = () => {
        if (!previewAudioRef.current) {
            previewAudioRef.current = new ((window as any).Audio)();
            (previewAudioRef.current as any).preload = "auto";
            (previewAudioRef.current as any).playsInline = true;
        }
        return previewAudioRef.current;
    };

    const stopPreview = () => {
        setPlayError("");
        if (previewAudioRef.current) {
            try { (previewAudioRef.current as any).pause(); (previewAudioRef.current as any).currentTime = 0; } catch { }
        }
    };

    const handlePlayError = (err: any) => {
        console.error("Audio play error:", err);
        const msg = String(err?.name || err?.message || err) || "";
        setPlayError(msg.includes("NotAllowedError") ? "Lecture bloquée. Re-clique pour autoriser." : "Impossible de lancer l'aperçu.");
    };

    const playPreview = (v: string) => {
        setPlayError("");
        const url = VOICE_PREVIEW_URLS[v] || null;
        if (!url) return setPlayError("Aucun aperçu disponible.");
        const a = ensurePreviewAudio();
        try {
            if (!(a as any).paused) { (a as any).pause(); (a as any).currentTime = 0; }
            (a as any).src = url;
            (a as any).play().catch(handlePlayError);
        } catch (err) { handlePlayError(err); }
    };

    const onVoiceGenerate = async ({ text, voice, language, tone, usePro, speed }: { text: string; voice: string; language: string; tone: string; usePro: boolean; speed: string }) => {
        if (!text.trim() || isGenerating) return;

        setIsGenerating(true);
        const newVoiceRequest: GeneratedVoice = { id: `gen-${Date.now()}`, text, voice, url: '', isLoading: true };
        setGeneratedVoices(prev => [newVoiceRequest, ...prev]);

        try {
            // Recherche de l'ID de la voix dans les presets
            const selectedPreset = VOICE_PRESETS.find(p => p.label === voice);
            const voiceName = selectedPreset ? selectedPreset.id : "Puck";
            const voiceStyle = selectedPreset ? selectedPreset.style : "Natural voice";

            // Détection du ton sélectionné
            const selectedTone = VOICE_TONES.find(t => t.id === tone);
            const toneInstruction = selectedTone && tone !== 'default'
                ? `Use a ${selectedTone.label.split('/')[0].trim()} tone.`
                : voiceStyle;

            // Détection de la langue sélectionnée
            const selectedLangFn = VOICE_LANGUAGES.find(l => l.code === language);
            const langLabel = selectedLangFn ? selectedLangFn.label : "English";

            // Détection de la vitesse sélectionnée
            const selectedSpeed = VOICE_SPEEDS.find(s => s.id === speed);
            const speedInstruction = selectedSpeed && speed !== 'normal' ? ` ${selectedSpeed.instruction}` : '';

            const promptText = `Instructions: You are a professional voice actor. ${toneInstruction}${speedInstruction}
            Language: Speak strictly in ${langLabel}.
            Text to read: "${text}"`;

            const payload = {
                model: usePro ? "gemini-2.5-pro-preview-tts" : "gemini-2.5-flash-preview-tts",
                contents: [{
                    role: 'user',
                    parts: [{ text: promptText }]
                }],
                config: {
                    responseModalities: [Modality.AUDIO],
                    speechConfig: {
                        voiceConfig: {
                            prebuiltVoiceConfig: { voiceName: voiceName },
                        },
                    },
                },
            };

            const response = await callGemini(payload);
            const geminiData = response.data as any;
            const base64Audio = geminiData.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;

            if (!base64Audio) {
                throw new Error("Aucune donnée audio retournée par l'API.");
            }

            const pcmData = decode(base64Audio);
            const mp3Blob = await pcmToMp3Blob(pcmData, 24000);
            const audioUrl = URL.createObjectURL(mp3Blob);

            const audio = new ((window as any).Audio)();
            audio.preload = 'metadata';
            audio.src = audioUrl;
            audio.onloadedmetadata = () => {
                setGeneratedVoices(prev => prev.map(v => v.id === newVoiceRequest.id ? { ...v, isLoading: false, url: audioUrl, duration: audio.duration } : v));
            };
            audio.onerror = () => {
                URL.revokeObjectURL(audioUrl);
                throw new Error("Échec du chargement des métadonnées audio.");
            };

        } catch (error: any) {
            console.error("La génération de la voix a échoué:", error);
            const errorMessage = error.message?.includes("Internal")
                ? "Erreur interne (modèle/backend)"
                : (error.code === 400 ? "Modèle ou paramètres invalides" : "Erreur de génération.");

            setGeneratedVoices(prev => prev.map(v => v.id === newVoiceRequest.id ? { ...v, isLoading: false, url: '', text: errorMessage } : v));
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div id="panel-voiceover" role="tabpanel" aria-labelledby="tab-voiceover" className="h-full flex flex-col">
            <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-900">Voiceover</h2>
                <p className="text-xs text-gray-500">Générer une voix off à partir d'un texte</p>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div>
                    <label htmlFor="voiceover-text" className="font-semibold mb-2 text-xs uppercase tracking-wide text-gray-500 block">Texte à lire</label>
                    <textarea id="voiceover-text" value={voiceoverText} onChange={e => setVoiceoverText((e.target as HTMLTextAreaElement).value)} placeholder="Saisissez le texte..." className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-sm resize-none bg-white text-gray-900 placeholder-gray-500" rows={5} />
                </div>
                <div className="space-y-2">
                    <label className="font-semibold text-xs uppercase tracking-wide text-gray-500 block">Voix</label>
                    {/* Gender Filter Buttons */}
                    <div className="flex gap-1 mb-2">
                        {VOICE_GENDERS.map(g => (
                            <button
                                key={g.id}
                                onClick={() => {
                                    setGenderFilter(g.id);
                                    // Reset voice if current voice is filtered out
                                    const newFiltered = g.id === "all" ? VOICE_PRESETS : VOICE_PRESETS.filter(v => v.gender === g.id);
                                    if (!newFiltered.find(v => v.label === voice)) {
                                        setVoice(newFiltered[0]?.label || VOICE_PRESETS[0].label);
                                    }
                                }}
                                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-lg transition-colors ${genderFilter === g.id
                                    ? 'bg-black text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                {g.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1" ref={voiceDropdownRef}>
                            <button onClick={() => setIsVoiceDropdownOpen(p => !p)} className="w-full flex items-center justify-between text-left p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent" aria-haspopup="true" aria-expanded={isVoiceDropdownOpen}>
                                <span className="text-sm font-medium">{voice}</span>
                                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isVoiceDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isVoiceDropdownOpen && (
                                <div className="absolute top-full mt-1 w-full max-h-60 overflow-y-auto bg-white shadow-lg rounded-lg border z-10 py-1">
                                    {filteredVoiceOptions.map(v => (
                                        <button
                                            key={v.id + v.label}
                                            onClick={() => { setVoice(v.label); setIsVoiceDropdownOpen(false); }}
                                            className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                        >
                                            <span className="mr-2">{v.icon}</span>
                                            {v.label}
                                            <span className="text-xs text-gray-400 ml-2">
                                                {v.gender === 'male' ? '👨' : '👩'}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button onClick={() => playPreview(voice)} className="p-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors flex-shrink-0" title="Écouter un aperçu"><Headphones className="w-5 h-5 text-gray-700" /></button>
                    </div>
                    {playError && <p className="text-xs text-red-600 mt-1">{playError}</p>}
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                        <label className="font-semibold text-xs uppercase tracking-wide text-gray-500 block">Langue</label>
                        <div className="relative">
                            <select value={language} onChange={e => setLanguage(e.target.value)} className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-sm bg-white appearance-none">
                                {VOICE_LANGUAGES.map(l => (
                                    <option key={l.code} value={l.code}>{l.label}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className="font-semibold text-xs uppercase tracking-wide text-gray-500 block">Ton / Style</label>
                        <div className="relative">
                            <select value={tone} onChange={e => setTone(e.target.value)} className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-sm bg-white appearance-none">
                                {VOICE_TONES.map(t => (
                                    <option key={t.id} value={t.id}>{t.label}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                    </div>
                </div>

                {/* Speed Control */}
                <div className="space-y-2">
                    <label className="font-semibold text-xs uppercase tracking-wide text-gray-500 block">Vitesse</label>
                    <div className="flex gap-1">
                        {VOICE_SPEEDS.map(s => (
                            <button
                                key={s.id}
                                onClick={() => setSpeed(s.id)}
                                className={`flex-1 px-3 py-2.5 text-sm font-medium rounded-xl transition-colors ${speed === s.id
                                    ? 'bg-black text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                                    }`}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="bg-gray-50 p-2 rounded-xl flex items-center justify-between border border-gray-200">
                    <span className="text-xs font-medium text-gray-600 ml-1">Modèle Rapide (Flash)</span>
                    <div
                        onClick={() => setUseProModel(!useProModel)}
                        className={`relative w-11 h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${useProModel ? 'bg-black' : 'bg-gray-300'}`}
                    >
                        <span
                            className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow transform transition-transform duration-200 ease-in-out ${useProModel ? 'translate-x-5' : 'translate-x-0'}`}
                        />
                    </div>
                    <span className={`text-xs font-medium mr-1 ${useProModel ? 'text-black font-bold' : 'text-gray-400'}`}>Modèle Pro (Qualité)</span>
                </div>

                <button onClick={() => onVoiceGenerate({ text: voiceoverText, voice, language, tone, usePro: useProModel, speed })} className="w-full bg-black hover:bg-gray-800 text-white px-4 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-50" disabled={!voiceoverText.trim() || isGenerating}>
                    {isGenerating ? <Loader2 className="w-5 h-5 animate-spin" /> : '🎙️ Générer la voix off'}
                </button>
                <div className="pt-4 mt-4 border-t border-gray-200">
                    <h3 className="font-semibold text-xs uppercase tracking-wide text-gray-500 mb-2">Audio généré</h3>
                    {generatedVoices.length === 0 ? (<div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed"><p className="text-sm text-gray-500">Vos voix off apparaîtront ici.</p></div>) : (<ul className="space-y-2">{generatedVoices.map(item => (<li key={item.id}><GeneratedVoiceItem voiceData={item} onDelete={(id) => {
                        const voiceToDelete = generatedVoices.find(v => v.id === id);
                        if (voiceToDelete?.url && voiceToDelete.url.startsWith('blob:')) {
                            URL.revokeObjectURL(voiceToDelete.url);
                        }
                        setGeneratedVoices(p => p.filter(v => v.id !== id));
                    }} onAddToTimeline={onAddLibraryItem!} onCopyText={(text) => setVoiceoverText(text)} /></li>))}</ul>)}
                </div>
            </div>
        </div>
    );
};

export default VoiceoverTab;
